import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { InlineNotification, RadioTile, TileGroup } from '@carbon/react';
import { useStore } from '@tanstack/react-store';

import { useWizardStep } from 'components/full-page-wizard';
import { DatePicker } from 'components/date-picker';
import { useBlobDownload } from 'lib/hooks/useBlobDownload';

import moment from 'moment';

import { packageSourceEntitySearchAtom } from '../atoms';
import { usePackageWizardCtx } from '../context';
import { useAssetListSource } from '../hooks';
import { PackageEntityTable } from '../package-entity-table';
import type { ScopePayload, SourceSelectionChange, SourceType } from '../types';
import {
  applySourceSelection,
  baselineAcquirers,
  canAdvanceStep0,
  markDraftTouched,
} from '../utils';
import { AssetListPanel } from './asset-list-panel';
import { SourcePackageTable } from './source-package-table';
import styles from './select-source-step.module.scss';

const EMPTY_IDS: Set<number> = new Set();

/** Props for {@link SelectSourceStep}. */
export interface SelectSourceStepProps {
  /**
   * Offer the From Asset List tile. Creating only: the source cannot be used
   * when editing a package, so the page passes its mode down for this alone.
   */
  offerAssetList?: boolean;
}

/**
 * Step 0 — Select Source. Collects the as-of date, the source type, and one
 * or more source rows from whichever table the type selects — or, on the
 * asset list, the pasted and uploaded Asset Names, checked as they change.
 *
 * Sync with the BE draft is delegated to `api.upsertDraft` (POST on first
 * sync, PATCH afterward) — this step doesn't know whether it's running in
 * create or edit mode. Pusher subscription, materialization wait, and
 * `draftId` writeback all live in the api closure; the step just `await`s.
 *
 * Skip-on-clean: TanStack form's `fieldMeta.isDirty` tracks whether
 * `sources` / `asOfDate` differ from the form's defaults. After every
 * successful upsert the api clears those bits, so back-navigating to Step 0
 * and clicking Next without changing anything is a true no-op (no PATCH
 * fires, no 422 from the BE's `at_least_one_change` validator).
 *
 * @param {SelectSourceStepProps} props - Whether to offer the asset list
 * @return {JSX.Element} Step 0 content
 */
export const SelectSourceStep: FC<SelectSourceStepProps> = ({
  offerAssetList = false,
}) => {
  const { form, api } = usePackageWizardCtx();
  const { setNextEnabled, setOnNext } = useWizardStep();

  const sourceType = useStore(form.store, (s) => s.values.sourceType);
  const sources = useStore(form.store, (s) => s.values.sources);
  const asOfDate = useStore(form.store, (s) => s.values.asOfDate);
  const assetList = useAssetListSource(form);
  const canProceed = useStore(form.store, (s) =>
    canAdvanceStep0(s.values, assetList.readiness)
  );

  // Locally-owned banner, cleared on the next successful sync — the upsert
  // atom's loadable carries the same state but is global and would persist a
  // stale message across mounts.
  const [draftError, setDraftError] = useState<string | null>(null);

  // Server-side CSV export of the entities table. The BE emits a
  // `Content-Disposition` filename via `send_data`; the supplied filename
  // is only a fallback. Filters aren't passed in the body — same shape
  // as the assets/slots exporters on Step 1.
  const { triggerDownload: handleEntityExport } = useBlobDownload({
    url: '/queries/entities/export',
    filename: 'entities.csv',
  });

  useEffect(() => {
    setNextEnabled(canProceed);
  }, [canProceed, setNextEnabled]);

  const handleNext = useCallback(async () => {
    const {
      sourceType: type,
      sources: rows,
      asOfDate: date,
    } = form.state.values;
    if (type === null || date === null) return;
    let scope: ScopePayload;
    if (type === 'assetList') {
      const { matched } = assetList.summary;
      if (matched.length === 0) return;
      scope = {
        asOfDate: date,
        sourceType: type,
        assetNames: matched.map(({ name }) => name),
      };
    } else {
      if (rows.length === 0) return;
      scope = {
        asOfDate: date,
        sourceType: type,
        sourceIds: rows.map((row) => row.id),
      };
    }
    // Whether to send anything is `api.upsertDraft`'s call (`step0DraftWrite`
    // compares the dirty Step 0 fields, which it clears after a successful
    // sync), so an unchanged Next sends nothing, on the asset list too, except
    // after a failed build of the list, which sends it again.
    try {
      await api.upsertDraft(scope);
      setDraftError(null);
    } catch (err) {
      setDraftError(
        err instanceof Error ? err.message : 'An unexpected error occurred'
      );
      throw err;
    }
  }, [form, api, assetList.summary]);

  useEffect(() => {
    setOnNext(handleNext);
  }, [setOnNext, handleNext]);

  /**
   * Handle source-tile change. The tiles stay mutually exclusive, so flipping
   * them clears the current selection, and the acquirers it pre-populated go
   * back to the ones the form opened with. No-op on the draft-touched flag
   * when nothing was picked. The asset list goes too — its text, file, check
   * answers and any upload in flight — so coming back starts it afresh.
   *
   * @param {SourceType} next - Newly selected tile type
   */
  const handleTileChange = (next: SourceType) => {
    if (next === form.state.values.sourceType) return;
    form.setFieldValue('sourceType', next);
    if (form.state.values.sources.length > 0) {
      form.setFieldValue('sources', []);
      form.setFieldValue('acquiringEntities', baselineAcquirers(form));
      markDraftTouched(form);
    }
    assetList.clear();
  };

  /**
   * Apply a selection delta from one of the source tables. The rule itself is
   * shared with the header's tag set — see `applySourceSelection`, which reads
   * the live form value rather than the rendered one so a burst of toggles
   * can't drop an update.
   *
   * @param {SourceType} type - Which table reported the change
   * @param {SourceSelectionChange} change - Rows to add / ids to drop
   */
  const applyChange = useCallback(
    (type: SourceType, change: SourceSelectionChange) =>
      applySourceSelection(form, type, change),
    [form]
  );

  const handleEntitySelectionChange = useCallback(
    (change: SourceSelectionChange) => applyChange('entity', change),
    [applyChange]
  );
  const handlePackageSelectionChange = useCallback(
    (change: SourceSelectionChange) => applyChange('package', change),
    [applyChange]
  );

  /**
   * Handle as-of-date change. Idempotent — skips the write when the next
   * value equals the current (Carbon DatePicker re-fires onChange on
   * reopen). Marks the draft touched only when the value actually moves
   * so reopening the picker doesn't spuriously flip the flag.
   *
   * @param {Date[]} dates - Carbon DatePicker selection
   */
  const handleAsOfDateChange = (dates: Date[]) => {
    const next = dates[0] ? moment(dates[0]).format('YYYY-MM-DD') : null;
    if (next === asOfDate) return;
    form.setFieldValue('asOfDate', next);
    markDraftTouched(form);
  };

  const selectedIds = useMemo(
    () => new Set(sources.map((source) => source.id)),
    [sources]
  );
  const selectedEntityIds = sourceType === 'entity' ? selectedIds : EMPTY_IDS;
  const selectedPackageIds = sourceType === 'package' ? selectedIds : EMPTY_IDS;

  const entityMultiSelect = useMemo(
    () => ({
      selectedIds: selectedEntityIds,
      onSelectionChange: handleEntitySelectionChange,
      active: sourceType === 'entity',
    }),
    [selectedEntityIds, handleEntitySelectionChange, sourceType]
  );

  return (
    <div className={styles.step}>
      {draftError !== null && (
        <InlineNotification
          kind="error"
          title="Couldn't sync package draft"
          subtitle={draftError}
          hideCloseButton
          lowContrast
          className={styles.errorNotification}
        />
      )}

      <div className={styles.dateRow}>
        <DatePicker
          id="package-wizard-as-of-date"
          labelText="Select As of Date *"
          value={asOfDate ?? undefined}
          onChange={handleAsOfDateChange}
          autoComplete="off"
        />
      </div>

      <TileGroup
        name="package-source-type"
        valueSelected={sourceType ?? ''}
        onChange={(value: string | number | undefined) =>
          handleTileChange(value as SourceType)
        }
        legend=""
        className={styles.tiles}
      >
        <RadioTile value="entity" className={styles.tile}>
          <span className={styles.tileLabel}>From Entity</span>
          <span className={styles.tileDesc}>
            Create a new package by selecting one or more entities
          </span>
        </RadioTile>
        <RadioTile value="package" className={styles.tile}>
          <span className={styles.tileLabel}>From Existing Package</span>
          <span className={styles.tileDesc}>
            Clone or combine one or more existing packages
          </span>
        </RadioTile>
        {offerAssetList && (
          <RadioTile value="assetList" className={styles.tile}>
            <span className={styles.tileLabel}>From Asset List</span>
            <span className={styles.tileDesc}>
              Paste or upload a list of Asset Names (T-0000000)
            </span>
          </RadioTile>
        )}
      </TileGroup>

      {/* Both tables stay mounted across tile flips — TanStack state
          (sort, column visibility/order/pinning, scroll position,
          search input) lives inside DataTable's internal store and is
          lost on unmount. Toggling visibility via CSS preserves it. */}
      <div
        className={classNames(styles.tableWrapper, {
          [styles.tableWrapperHidden]: sourceType !== 'entity',
        })}
      >
        <PackageEntityTable
          searchAtom={packageSourceEntitySearchAtom}
          multiSelect={entityMultiSelect}
          onExport={handleEntityExport}
        />
      </div>
      <div
        className={classNames(styles.tableWrapper, {
          [styles.tableWrapperHidden]: sourceType !== 'package',
        })}
      >
        <SourcePackageTable
          selectedIds={selectedPackageIds}
          onSelectionChange={handlePackageSelectionChange}
          active={sourceType === 'package'}
        />
      </div>
      {sourceType === 'assetList' && <AssetListPanel {...assetList.panel} />}
    </div>
  );
};
